import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';

import { DEFAULT_LANG } from '~constants';
import { LocaleService } from '~shared/services/locale.service';
import { DatesUtils } from '~shared/utils/dates.utils';

import {
  TOOLTIP_DISMISS_KEY,
  TOOLTIP_OBSERVED_ATTRIBUTES,
} from './tooltip.constants';
import {
  DateTimeTooltipFormatter,
  TooltipTrigger,
  findTooltipTrigger,
  neutralizeNativeTitle,
  readTooltipText,
} from './tooltip-trigger.utils';
import { TooltipService } from './tooltip.service';

/**
 * Branche l'infobulle sur TOUTE l'application, sans repasser par les gabarits.
 *
 * Une poignée d'écouteurs posés une fois sur `document` (délégation
 * d'événements) suffit à couvrir chaque icône, y compris celles des
 * bibliothèques tierces et celles créées après coup. Ils tournent HORS de la
 * zone Angular : survoler un tableau de mille lignes ne déclenche aucune
 * détection de changements.
 *
 * Est une infobulle (cf. `tooltip-trigger.utils.ts`) : un `appTooltip`, un
 * `appDateTimeTooltip`, tout `title` natif (neutralisé au passage pour ne pas
 * doubler la bulle du navigateur), et l'`aria-label` d'un bouton ou lien qui
 * n'affiche qu'une icône.
 *
 * Apparition : immédiate au survol, et à la prise de focus AU CLAVIER (un clic
 * donne aussi le focus, mais l'utilisateur vient alors d'agir : la bulle ne
 * revient pas). Disparition : sortie du survol, perte du focus, `Échap`, clic,
 * défilement.
 */
@Injectable({ providedIn: 'root' })
export class TooltipDelegateService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly tooltip = inject(TooltipService);
  private readonly localeService = inject(LocaleService);

  private readonly removeListeners: Array<() => void> = [];
  private anchorObserver: MutationObserver | null = null;
  /** Le dernier focus vient-il d'un clic (et non du clavier) ? */
  private focusFromPointer = false;

  private readonly formatDateTime: DateTimeTooltipFormatter = (raw) =>
    DatesUtils.fullDateTime(
      raw,
      this.localeService.getLanguage() || DEFAULT_LANG
    );

  /** Idempotent : un second appel ne double pas les écouteurs. */
  start(): void {
    if (this.removeListeners.length) return;
    this.zone.runOutsideAngular(() => {
      this.listen('mouseover', (event) => this.onPointerOver(event));
      this.listen('mouseout', (event) => this.onPointerOut(event));
      this.listen('focusin', (event) => this.onFocusIn(event));
      this.listen('focusout', (event) => this.onFocusOut(event));
      this.listen('keydown', (event) => this.onKeydown(event));
      this.listen('pointerdown', () => this.onPointerDown());
      this.listen('scroll', () => this.hide());
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.splice(0).forEach((remove) => remove());
    this.hide();
  }

  private listen<K extends keyof DocumentEventMap>(
    type: K,
    handler: (event: DocumentEventMap[K]) => void
  ): void {
    // Phase de capture : un composant qui stoppe la propagation ne doit pas
    // rendre son icône muette.
    const options: AddEventListenerOptions = { capture: true, passive: true };
    this.document.addEventListener(type, handler, options);
    this.removeListeners.push(() =>
      this.document.removeEventListener(type, handler, options)
    );
  }

  private onPointerOver(event: MouseEvent): void {
    const trigger = this.triggerFor(event.target);
    if (!trigger) {
      this.hide();
      return;
    }
    if (trigger.anchor !== this.tooltip.currentAnchor) this.show(trigger);
  }

  private onPointerOut(event: MouseEvent): void {
    // Pointeur sorti de la fenêtre : aucun `mouseover` ne suivra.
    if (event.relatedTarget === null) this.hide();
  }

  private onFocusIn(event: FocusEvent): void {
    if (this.focusFromPointer) return;
    const trigger = this.triggerFor(event.target);
    if (trigger?.anchor === event.target) this.show(trigger);
  }

  private onFocusOut(event: FocusEvent): void {
    if (event.target === this.tooltip.currentAnchor) this.hide();
  }

  private onKeydown(event: KeyboardEvent): void {
    this.focusFromPointer = false;
    if (event.key === TOOLTIP_DISMISS_KEY) this.hide();
  }

  private onPointerDown(): void {
    this.focusFromPointer = true;
    this.hide();
  }

  private triggerFor(target: EventTarget | null): TooltipTrigger | null {
    return target instanceof Element
      ? findTooltipTrigger(target, this.formatDateTime)
      : null;
  }

  private show({ anchor, text }: TooltipTrigger): void {
    this.tooltip.show(anchor, text);
    this.observe(anchor);
  }

  private hide(): void {
    this.anchorObserver?.disconnect();
    this.anchorObserver = null;
    if (this.tooltip.currentAnchor) this.tooltip.hide();
  }

  /**
   * Suit l'élément affiché : un libellé qui change pendant le survol
   * (verrouiller → déverrouiller) met la bulle à jour, et un `title` reposé
   * par Angular est neutralisé avant que le navigateur ne l'affiche.
   */
  private observe(anchor: Element): void {
    this.anchorObserver?.disconnect();
    this.anchorObserver = new MutationObserver(() => {
      neutralizeNativeTitle(anchor);
      const text = readTooltipText(anchor, this.formatDateTime);
      if (text) this.tooltip.show(anchor, text);
      else this.hide();
    });
    this.anchorObserver.observe(anchor, {
      attributes: true,
      attributeFilter: [...TOOLTIP_OBSERVED_ATTRIBUTES],
    });
  }
}
